import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  Bank, Check, Crown, FileText, GoogleLogo, Notebook, Package, Receipt, ShoppingCart,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr, inr } from "../lib/api";
import { HOME_CONTENT_DEFAULTS, normalizeHomepageContent } from "../lib/siteContent";
import { useAuth } from "../context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const FEATURE_VISUALS = [
  { icon: Receipt, color: "bg-blue-50 text-blue-700", size: "md:col-span-2" },
  { icon: Package, color: "bg-amber-50 text-amber-700" },
  { icon: FileText, color: "bg-violet-50 text-violet-700" },
  { icon: ShoppingCart, color: "bg-emerald-50 text-emerald-700" },
  { icon: Notebook, color: "bg-pink-50 text-pink-700" },
  { icon: Bank, color: "bg-cyan-50 text-cyan-700", size: "md:col-span-2" },
  { icon: FileText, color: "bg-orange-50 text-orange-700" },
  { icon: ShoppingCart, color: "bg-indigo-50 text-indigo-700" },
  { icon: Receipt, color: "bg-teal-50 text-teal-700" },
];

const BENEFIT_VISUALS = [
  { icon: Check },
  { icon: Crown },
  { icon: Bank },
  { icon: FileText },
];

export default function Landing() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [homepage, setHomepage] = useState(HOME_CONTENT_DEFAULTS);
  const [signupOpen, setSignupOpen] = useState(false);
  const [planIntent, setPlanIntent] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [plansError, setPlansError] = useState(false);
  const [postSignupTarget, setPostSignupTarget] = useState(null);
  const [contact, setContact] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [contactBusy, setContactBusy] = useState(false);
  const [contactError, setContactError] = useState("");
  const [contactSent, setContactSent] = useState(false);

  useEffect(() => {
    API.get("/public/plans")
      .then((r) => setPlans(r.data))
      .catch(() => setPlansError(true));
    API.get("/public/site-content")
      .then(({ data }) => setHomepage(normalizeHomepageContent(data.homepage)))
      .catch((error) => toast.error(fmtErr(error)));
  }, []);

  if (user) return <Navigate to={postSignupTarget || "/dashboard"} replace />;

  const openSignup = (planKey = null) => {
    setPlanIntent(planKey);
    setError("");
    setSignupOpen(true);
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    if (!accepted) {
      setError("Please accept the Terms & Conditions and Privacy Policy to continue.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const r = await API.post("/auth/register", { name, email, password, terms_accepted: true });
      const target = planIntent && planIntent !== "free" ? `/billing?plan=${planIntent}` : "/dashboard";
      setPostSignupTarget(target);
      setUser(r.data);
      toast.success(`Welcome, ${r.data.name}! Your free account is ready.`);
      navigate(target);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setContactBusy(true);
    setContactError("");
    setContactSent(false);
    try {
      await API.post("/contact", contact);
      setContact({ name: "", email: "", subject: "", message: "", website: "" });
      setContactSent(true);
    } catch (err) {
      setContactError(fmtErr(err, "Your message could not be sent. Please try again."));
    } finally {
      setContactBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const features = FEATURE_VISUALS.map((visual, index) => ({
    ...visual,
    ...homepage.features[index],
  }));
  const benefits = BENEFIT_VISUALS.map((visual, index) => ({
    ...visual,
    ...homepage.benefits[index],
  }));

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <header className="sticky top-0 z-20 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <a href="#home" className="flex items-center gap-2 font-heading text-lg font-extrabold tracking-tight text-slate-900" data-testid="landing-brand">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-sm font-black text-white">A</span>
            ADS Billing
          </a>
          <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex" aria-label="Main navigation">
            <a href="#home" className="transition-colors hover:text-emerald-700">Home</a>
            <a href="#workspace-preview" className="transition-colors hover:text-emerald-700">Demo</a>
            <a href="#features" className="transition-colors hover:text-emerald-700">Features</a>
            <a href="#why-ads-billing" className="transition-colors hover:text-emerald-700">Why ADS Billing</a>
            <a href="#pricing" className="transition-colors hover:text-emerald-700">Pricing</a>
            <a href="#contact" className="transition-colors hover:text-emerald-700">Contact</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="outline" size="sm" data-testid="landing-login-link">Login</Button>
            </Link>
            <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => openSignup("free")} data-testid="landing-signup-button">Sign Up</Button>
          </div>
        </div>
      </header>

      <main>
      <section id="home" className="overflow-hidden bg-gradient-to-b from-emerald-50/80 via-white to-white">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-2 lg:gap-16 lg:py-24">
          <div className="animate-fade-up">
            <Badge variant="outline" className="border-emerald-200 bg-white text-emerald-800">{homepage.hero_badge}</Badge>
            <h1 className="mt-5 max-w-2xl font-heading text-4xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              {homepage.hero_heading} <span className="text-emerald-700">{homepage.hero_highlight}</span>
            </h1>
            <p className="mt-5 max-w-xl text-base leading-7 text-slate-600 sm:text-lg">
              {homepage.hero_description}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button size="lg" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => openSignup("free")} data-testid="hero-signup-button">
                {homepage.hero_primary_cta} <span className="ml-2" aria-hidden="true">→</span>
              </Button>
              <Button size="lg" variant="outline" className="border-slate-300 bg-white" onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-pricing-button">
                {homepage.hero_secondary_cta}
              </Button>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
              <span className="inline-flex items-center gap-1.5"><Check size={16} weight="bold" className="text-emerald-600" /> Start free</span>
              <span className="inline-flex items-center gap-1.5"><Check size={16} weight="bold" className="text-emerald-600" /> GST-ready reports</span>
              <span className="inline-flex items-center gap-1.5"><Check size={16} weight="bold" className="text-emerald-600" /> Simple setup</span>
            </div>
          </div>
          <div id="workspace-preview" className="relative mx-auto w-full max-w-xl animate-fade-up" data-testid="landing-dashboard-preview">
            <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-emerald-200/40 blur-3xl" />
            <div className="absolute -bottom-8 -left-8 h-40 w-40 rounded-full bg-amber-200/50 blur-3xl" />
            <div className="relative rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl shadow-slate-900/10 sm:p-5">
              <div className="flex items-center justify-between border-b pb-3">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-600 text-sm font-bold text-white">A</span>
                  <div><p className="text-xs font-bold text-slate-800">ADS Billing</p><p className="text-[10px] text-slate-500">Sample dashboard</p></div>
                </div>
                <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700">This month</span>
              </div>
              <div className="grid grid-cols-3 gap-2 py-4 sm:gap-3">
                {[
                  ["Sales", "₹2,48,500", "bg-emerald-50 text-emerald-700"],
                  ["Invoices", "128", "bg-blue-50 text-blue-700"],
                  ["Outstanding", "₹34,200", "bg-amber-50 text-amber-700"],
                ].map(([label, value, color]) => (
                  <div key={label} className="rounded-xl border border-slate-100 p-2.5 sm:p-3">
                    <p className="text-[10px] text-slate-500 sm:text-xs">{label}</p>
                    <p className={`mt-1 truncate text-xs font-bold sm:text-base ${color.split(" ")[1]}`}>{value}</p>
                  </div>
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-[1.5fr_1fr]">
                <div className="rounded-xl border border-slate-100 p-3">
                  <div className="flex items-center justify-between"><p className="text-xs font-bold text-slate-800">Sales activity</p><span className="text-[10px] text-slate-400">Last 7 days</span></div>
                  <div className="mt-4 flex h-28 items-end gap-2 border-b border-slate-100 px-1">
                    {[38, 62, 46, 79, 55, 91, 68, 100, 74, 84, 58, 90].map((height, index) => (
                      <span key={index} className={`flex-1 rounded-t-sm ${index === 7 ? "bg-emerald-600" : "bg-emerald-200"}`} style={{ height: `${height}%` }} />
                    ))}
                  </div>
                  <div className="mt-2 flex justify-between text-[9px] text-slate-400"><span>Mon</span><span>Wed</span><span>Fri</span><span>Sun</span></div>
                </div>
                <div className="rounded-xl border border-slate-100 p-3">
                  <p className="text-xs font-bold text-slate-800">Quick access</p>
                  <div className="mt-3 space-y-2">
                    {[["New invoice", "bg-blue-50 text-blue-700"], ["Add purchase", "bg-amber-50 text-amber-700"], ["View reports", "bg-violet-50 text-violet-700"]].map(([label, color]) => (
                      <div key={label} className="flex items-center gap-2 rounded-lg bg-slate-50 p-2 text-[10px] font-medium text-slate-700">
                        <span className={`h-2 w-2 rounded-full ${color.split(" ")[0]}`} />{label}
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 rounded-lg bg-emerald-50 p-2 text-[10px] text-emerald-800">Your books are up to date</div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-[10px]">
                <span className="font-medium text-slate-600">Recent invoice · INV-0128</span>
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-700">Paid</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">Everything in one place</p>
          <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Tools to keep business moving</h2>
          <p className="mt-3 text-slate-600">From your first invoice to everyday reporting, ADS Billing helps bring the essentials together.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
          {features.map((f, i) => (
            <div
              key={f.title}
              className={`animate-fade-up rounded-2xl border border-slate-100 ${f.color.split(" ")[0]} p-5 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg sm:p-6 ${f.size || ""}`}
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="max-w-xs">
                  <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white ${f.color.split(" ")[1]} shadow-sm`}><f.icon size={20} weight="bold" /></span>
                  <h3 className="mt-4 font-heading font-bold text-slate-900">{f.title}</h3>
                  <p className="mt-1 text-sm leading-6 text-slate-600">{f.description}</p>
                </div>
                <f.icon size={56} weight="thin" className={`${f.color.split(" ")[1]} hidden opacity-25 sm:block`} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="why-ads-billing" className="bg-emerald-50/70 py-16 sm:py-20">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.85fr_2fr] lg:items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">Why choose us</p>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-slate-900">More clarity in your daily work.</h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">Bring routine billing and business records together with tools designed to be clear and practical.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="flex gap-4 rounded-2xl border border-white bg-white p-5 shadow-sm">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><benefit.icon size={22} weight="bold" /></span>
                <div><h3 className="font-heading font-bold text-slate-900">{benefit.title}</h3><p className="mt-1 text-sm leading-5 text-slate-600">{benefit.description}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 rounded-2xl bg-slate-900 px-6 py-8 text-center text-white sm:flex-row sm:px-10 sm:text-left">
          <div><h2 className="font-heading text-2xl font-bold">{homepage.cta_heading}</h2><p className="mt-1 text-sm text-slate-300">{homepage.cta_description}</p></div>
          <Button size="lg" className="shrink-0 bg-amber-400 font-bold text-slate-900 hover:bg-amber-300" onClick={() => openSignup("free")}>{homepage.cta_button} <span className="ml-2" aria-hidden="true">→</span></Button>
        </div>
      </section>

      <section id="pricing" className="border-t bg-muted/30 py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">Simple plans, honest pricing</h2>
            <p className="mt-3 text-muted-foreground">Start free. Upgrade when your invoice volume grows. Every account starts on the Free plan automatically.</p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
            {plansError && (
              <p className="col-span-full rounded-md border border-[#E02424]/30 bg-[#E02424]/5 px-4 py-3 text-sm text-[#E02424]" data-testid="plans-load-error">
                Plans could not be loaded right now. Please refresh, or sign up free and pick a plan later.
              </p>
            )}
            {plans.map((p) => (
              <div
                key={p.key}
                className={`flex flex-col rounded-lg border bg-card p-6 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md ${p.key === "pro" ? "border-primary ring-1 ring-primary" : ""}`}
                data-testid={`landing-plan-${p.key}`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold">{p.name}</h3>
                  {p.key === "pro" && <Badge className="bg-primary">Popular</Badge>}
                </div>
                <p className="mt-3 font-heading text-3xl font-bold tabular-nums">
                  {p.price_monthly === 0 ? "Free" : inr(p.price_monthly)}
                  {p.price_monthly > 0 && <span className="text-sm font-normal text-muted-foreground"> /month</span>}
                </p>
                {p.price_yearly > 0 && (
                  <p className="text-xs text-muted-foreground">or {inr(p.price_yearly)}/year</p>
                )}
                {p.price_monthly > 0 && (
                  <p className="mt-1 text-xs text-muted-foreground" data-testid={`landing-gst-note-${p.key}`}>+ 18% GST extra at checkout</p>
                )}
                <ul className="mt-4 flex-1 space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check size={15} className="text-[#008A52]" />
                    {p.invoice_limit === 0 ? "Unlimited" : p.invoice_limit} invoices per month
                  </li>
                  <li className="flex items-center gap-2">
                    <Check size={15} className="text-[#008A52]" /> GST reports &amp; P&amp;L exports
                  </li>
                  <li className="flex items-center gap-2">
                    <Check size={15} className={p.features?.statement_import ? "text-[#008A52]" : "text-muted-foreground/40"} />
                    <span className={p.features?.statement_import ? "" : "text-muted-foreground line-through"}>Bank statement import</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check size={15} className={(p.features?.invoice_email ?? true) ? "text-[#008A52]" : "text-muted-foreground/40"} />
                    <span className={(p.features?.invoice_email ?? true) ? "" : "text-muted-foreground line-through"}>Email invoices to customers</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check size={15} className={p.features?.custom_invoice_sender ? "text-[#008A52]" : "text-muted-foreground/40"} />
                    <span className={p.features?.custom_invoice_sender ? "" : "text-muted-foreground line-through"}>Use your own invoice sender email</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check size={15} className={Number(p.features?.branch_limit || 0) > 0 ? "text-[#008A52]" : "text-muted-foreground/40"} />
                    <span className={Number(p.features?.branch_limit || 0) > 0 ? "" : "text-muted-foreground line-through"}>
                      {Number(p.features?.branch_limit || 0)} additional branches
                    </span>
                  </li>
                </ul>
                <Button
                  className="mt-6"
                  variant={p.price_monthly === 0 ? "outline" : "default"}
                  onClick={() => openSignup(p.key)}
                  data-testid={`landing-plan-${p.key}-button`}
                >
                  {p.price_monthly === 0 ? "Start Free" : (
                    <><Crown size={15} className="mr-1" /> Get {p.name}</>
                  )}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="border-t bg-white py-16 sm:py-20">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 md:grid-cols-2">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">Contact</p>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight text-slate-900">How can we help?</h2>
            <p className="mt-3 max-w-md text-sm leading-6 text-slate-600">
              Send a message to our team and we’ll get back to you. You can also email{" "}
              <a href="mailto:support@adsbilling.com" className="font-medium text-emerald-700 hover:underline">support@adsbilling.com</a>.
            </p>
          </div>
          <form className="space-y-4" onSubmit={submitContact} data-testid="contact-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="contact-name">Name</Label>
                <Input id="contact-name" required minLength={2} maxLength={100} value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="contact-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email">Email</Label>
                <Input id="contact-email" type="email" required maxLength={320} value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="contact-email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-subject">Subject <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <Input id="contact-subject" maxLength={160} value={contact.subject} onChange={(e) => setContact({ ...contact, subject: e.target.value })} data-testid="contact-subject-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-message">Message</Label>
              <Textarea id="contact-message" required minLength={10} maxLength={5000} rows={5} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} data-testid="contact-message-input" />
            </div>
            <div className="sr-only" aria-hidden="true">
              <Label htmlFor="contact-website">Leave this field empty</Label>
              <Input id="contact-website" tabIndex={-1} autoComplete="off" value={contact.website} onChange={(e) => setContact({ ...contact, website: e.target.value })} />
            </div>
            {contactError && <p className="text-sm font-medium text-destructive" role="alert" data-testid="contact-error">{contactError}</p>}
            {contactSent && <p className="text-sm font-medium text-emerald-700" role="status" data-testid="contact-success">Your message has been sent. Thank you for contacting us.</p>}
            <Button type="submit" disabled={contactBusy} data-testid="contact-submit-button">
              {contactBusy ? "Sending…" : "Send message"}
            </Button>
          </form>
        </div>
      </section>
      </main>

      <footer className="border-t py-10">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 sm:px-6">
          <span className="font-heading font-bold text-primary">ADS Billing</span>
          <p className="text-xs text-muted-foreground">GST billing and accounting — sales, purchases, inventory, banking &amp; reports.</p>
          <nav className="flex flex-wrap items-center gap-4 text-sm" data-testid="landing-legal-links">
            <a href="#contact" className="text-muted-foreground hover:text-primary hover:underline">Contact</a>
            <Link to="/terms" className="text-muted-foreground hover:text-primary hover:underline" data-testid="footer-terms-link">Terms &amp; Conditions</Link>
            <Link to="/privacy" className="text-muted-foreground hover:text-primary hover:underline" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/refund" className="text-muted-foreground hover:text-primary hover:underline" data-testid="footer-refund-link">Refund Policy</Link>
            <Link to="/login" className="font-medium text-primary hover:underline">Sign In</Link>
          </nav>
        </div>
      </footer>

      <Dialog open={signupOpen} onOpenChange={setSignupOpen}>
        <DialogContent className="max-w-sm" data-testid="landing-signup-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Create your account</DialogTitle>
            <DialogDescription className="sr-only">Sign up for a free ADS Billing account</DialogDescription>
          </DialogHeader>
          {planIntent && planIntent !== "free" && (
            <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm" data-testid="signup-plan-intent">
              You picked <span className="font-semibold capitalize">{planIntent}</span> — sign up first, then complete payment on the Subscription page.
            </p>
          )}
          <form className="space-y-4" onSubmit={submitSignup}>
            <div className="space-y-2">
              <Label htmlFor="su-name">Full Name</Label>
              <Input id="su-name" data-testid="signup-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="su-email">Email</Label>
              <Input id="su-email" data-testid="signup-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.in" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="su-password">Password</Label>
              <Input id="su-password" data-testid="signup-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" />
            </div>
            {error && <p className="text-sm font-medium text-destructive" data-testid="signup-error">{error}</p>}
            <label className="flex items-start gap-2 text-xs text-muted-foreground" data-testid="signup-terms-label">
              <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-0.5" data-testid="signup-terms-checkbox" />
              <span>
                I agree to the <Link to="/terms" target="_blank" className="font-medium text-primary hover:underline">Terms &amp; Conditions</Link> and{" "}
                <Link to="/privacy" target="_blank" className="font-medium text-primary hover:underline">Privacy Policy</Link>.
              </span>
            </label>
            <Button type="submit" className="w-full" disabled={busy} data-testid="signup-submit-button">
              {busy ? "Creating…" : "Sign Up — It's Free"}
            </Button>
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">or</span>
              </div>
            </div>
            <Button type="button" variant="outline" className="w-full" onClick={googleLogin} data-testid="landing-google-button">
              <GoogleLogo size={18} className="mr-2" /> Continue with Google
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
